import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { Circle } from 'lucide-react';
import { cva, VariantProps } from 'class-variance-authority';
import { OmitStylesComponentProps } from '../../types';
import { cn } from '../../utils/cn';

const radioGroupItemVariants = cva(
    'rocco-radio-group-item flex aspect-square h-2.5 w-2.5 cursor-pointer items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'rounded-full border border-primary-foreground text-primary-foreground ring-offset-background',
            },
            color: {
                default: '',
                brick: 'border text-brick-500 ring-offset-background',
            },
            background: {
                default: '',
                gray: 'bg-gray-300',
                none: 'bg-transparent',
            },
            size: {
                medium: 'h-3.5 w-3.5',
                default: '',
            },
            selectedBackground: {
                default: '',
                black: '[&[data-state=checked]]:bg-black',
            },
            border: {
                default: 'border-gray-900',
                none: 'border-0',
            },
        },
        defaultVariants: {
            variant: 'default',
            color: 'default',
            background: 'default',
            size: 'default',
            selectedBackground: 'default',
            border: 'default',
        },
    }
);

export const RadioGroupItem = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Item>,
    OmitStylesComponentProps<typeof RadioGroupPrimitive.Item> &
        VariantProps<typeof radioGroupItemVariants> & {
            iconClasssNames?: string;
        }
>(
    (
        {
            variant,
            color,
            background,
            size,
            iconClasssNames,
            selectedBackground,
            border,
            ...props
        },
        ref
    ) => {
        return (
            <RadioGroupPrimitive.Item
                ref={ref}
                className={radioGroupItemVariants({
                    variant,
                    color,
                    background,
                    size,
                    selectedBackground,
                    border,
                })}
                {...props}
            >
                <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                    <Circle
                        className={cn(
                            'h-2.5 w-2.5 fill-current text-current',
                            iconClasssNames
                        )}
                    />
                </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
        );
    }
);

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
